import React from 'react';
import styled, { css } from 'styled-components';
import { DutchiePayButton } from 'src/payments/components';
import _ from 'lodash';
import useTranslation from 'src/hooks/use-translation';
import { centsToDollars } from 'src/payments/utils';
import { OrderProcessingIcon } from 'assets/order-processing-icon';
import { Text } from '@dutchie/common/components/rebass-compat';

export type PayNowButtonProps = React.HTMLProps<HTMLButtonElement> & {
  loading?: boolean;
  disabled?: boolean;
  // The total in cents
  total?: number;
};

export const PayNowButton = (props: PayNowButtonProps): JSX.Element => {
  const { onClick = () => _.constant(null), loading = true, total = 0, disabled = false } = props;
  const { t } = useTranslation();
  const priceFormatted = centsToDollars(total);
  return (
    <>
      {loading && <Overlay />}
      <Container>
        <ButtonContainer>
          {loading && (
            <>
              <OrderProcessingIcon style={{ marginBottom: '24px' }} />
              <DrawerHeader>{t('dutchie.in-store.pay-now-processing', "We're processing your payment")}</DrawerHeader>
              <DrawerSubHeader>
                {t('dutchie.in-store.pay-now-browser-message', "Please don't close your browser!")}
              </DrawerSubHeader>
            </>
          )}
          <DutchiePayButton
            data-testid='confirm-instore-payment'
            width='335px'
            onClick={onClick}
            loading={loading}
            disabled={disabled}
          >
            <div>{t('dutchie.in-store.pay-now-confirm', 'CONFIRM')}</div>
            <div>{priceFormatted}</div>
          </DutchiePayButton>
        </ButtonContainer>
      </Container>
    </>
  );
};

const Overlay = styled.div`
  background-color: black;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  height: 100vh;
  width: 100%;
  opacity: 0.2;
`;

const DrawerHeader = styled(Text)`
  width: 100%;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
`;

const DrawerSubHeader = styled(Text)`
  ${({ theme: { spaces } }) => css`
    width: 100%;
    text-align: center;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    padding: ${spaces[1]} 0 ${spaces[6]} 0;
  `}
`;

const ButtonContainer = styled.div`
  ${({ theme: { spaces } }) => css`
    width: 100%;
    max-width: var(--dp-content-width, 450px);
    padding: ${spaces[6]};
    display: flex;
    flex-direction: column;
    align-items: center;
  `}
`;

const Container = styled.div`
  ${({ theme: { colors } }) => css`
    background-color: ${colors.white};
    position: fixed;
    bottom: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    width: 100%;
    border-top: 1px solid ${colors.grey[90]};
    box-shadow: 0px -4px 16px rgba(0, 0, 0, 0.08);
  `}
`;
